/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { isError } from "@epic-mw/error-types";

import { getApiPath } from "../utils";

export interface AuthAccount {
	isLoggedIn: boolean;
	country?: string;
	displayName?: string;
	email?: string;
	id?: string;
	preferredLanguage?: string;
	cabinedMode?: boolean;
}

/** Mirrors `SilentLogin` in `@cosmos/next-app-token`, which this package does not depend on. */
export interface SilentLoginOffer {
	/** The auth worker's `/auth/login?prompt=none` on this origin, without a `returnPath`. */
	url: string;
	/** Identifies the Account Portal SSO cookies behind the offer; attempted once per value. */
	fingerprint: string;
}

export interface AuthRes {
	bearerTokenValid: boolean;
	clearedOffline: boolean;
	accountId: string;
	upgradedBearerToken: boolean;
	/**
	 * The account as the server sees it after any repair. Present so the client
	 * does not need a second call to `/account`, and so it never disagrees with
	 * the session this same request just validated.
	 */
	account?: AuthAccount;
	/** True when this request found a dead session and cleared it. */
	repairedSession?: boolean;
	/**
	 * An offer to sign in silently, present only on a signed-out response when the user is signed
	 * in to Epic on another domain. See `useSilentLogin`.
	 */
	silentLogin?: SilentLoginOffer;
	/**
	 * The Account Portal SSO fingerprint behind the current session, present only on a signed-in
	 * response. Remembered so that after signing out here the tab does not follow the same cookies
	 * back in. Mirrors `resolveSsoFingerprint` in `@cosmos/next-app-token`.
	 */
	ssoFingerprint?: string;
}

export const auth = async (serverUrl?: string): Promise<AuthRes> => {
	try {
		console.log("auth", { serverUrl: `${getApiPath(serverUrl)}/auth` });
		const response = await fetch(`${getApiPath(serverUrl)}/auth`);
		const json: AuthRes = await response.json();
		return json;
	} catch (err) {
		console.error("auth() failed to load", isError(err) && err.message);
		throw err;
	}
};
